import { Component, type ErrorInfo, type ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/Button";
import { MobileBottomNav } from "./MobileBottomNav";
import { OfflineBanner } from "./OfflineBanner";
import { Sidebar } from "./Sidebar";
import { StatusBar } from "./StatusBar";
import { TopBar } from "./TopBar";
import { useSidebar } from "./useSidebar";

/**
 * The application shell (TICKET FE-001).
 *
 * Sidebar on desktop, bottom navigation on mobile, one scrolling content area.
 * The bottom padding on mobile keeps the last row clear of the nav bar and of
 * the iPhone home indicator.
 */
export function AppShell() {
  const { collapsed, toggle } = useSidebar();

  return (
    <div className="flex h-dvh flex-col overflow-x-hidden bg-canvas">
      <div className="flex min-h-0 min-w-0 flex-1">
        <Sidebar collapsed={collapsed} onToggle={toggle} />
        {/* The content column is the container the pages inside it measure
            themselves against. It is the real space they have — the window
            minus the sidebar — which is not the same number as the viewport,
            and that difference is a whole table column. */}
        <div className="@container flex min-w-0 flex-1 flex-col">
          <TopBar />
          {/* Above the content, below the search: it must be impossible to miss
              and impossible to mistake for part of the screen you are on. */}
          <OfflineBanner />
          {/* The one vertical scroll of the application. min-h-0 is what lets it
              fill the remaining height instead of pushing the shell past the
              viewport; overflow-x-hidden means a component that misbehaves
              scrolls inside itself instead of sliding the whole page sideways.
              pb-24 clears the mobile bottom bar and the iPhone home indicator. */}
          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden px-3 pb-24 pt-4 md:px-4 md:pb-4 desk:px-5">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
      {/* Full width at the very bottom, the way a WMS status bar sits. */}
      <StatusBar />
      <MobileBottomNav />
    </div>
  );
}

interface BoundaryState {
  error: Error | null;
}

/**
 * Catches a render crash so one broken screen does not take the whole app with
 * it — a technician mid-count should still be able to navigate away.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("HD-Vault: render error", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return <>{this.props.children}</>;
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center">
        <span className="rounded-full bg-danger-subtle p-3 text-danger">
          <AlertTriangle className="size-6" aria-hidden />
        </span>
        <p className="text-sm font-medium text-ink">Cet écran a rencontré une erreur</p>
        <p className="text-[13px] text-ink-subtle">{this.state.error.message}</p>
        <Button variant="secondary" size="sm" onClick={() => this.setState({ error: null })}>
          Réessayer
        </Button>
      </div>
    );
  }
}
